import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Check, Sparkles } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { FadeUp } from "@/components/Reveal";
import Breadcrumbs from "@/components/Breadcrumbs";

/* --------------------------------------------------------------- */
/* Data. 6 NANDA SÁ signature references (matches the catalogue) */
/* --------------------------------------------------------------- */
const SKUS = [
 {
 id: "chantilly",
 name: { fr: "Chantilly Karité & Coco", en: "Chantilly Karité & Coco", pt: "Chantilly Karité & Coco" },
 category: { fr: "Soin corps", en: "Body care", pt: "Cuidado corpo" },
 image: "/photos/nanda-cosmetiques/chantilly-karite-coco.png",
 default: true,
 },
 {
 id: "karite-brut",
 name: { fr: "Beurre de Karité brut", en: "Raw Shea Butter", pt: "Manteiga de Karité pura" },
 category: { fr: "Soin corps", en: "Body care", pt: "Cuidado corpo" },
 image: "/photos/nanda-cosmetiques/ing-shea-hero.jpg",
 default: false,
 },
 {
 id: "aloe",
 name: { fr: "Gel d'Aloe Vera", en: "Aloe Vera Gel", pt: "Gel de Aloé Vera" },
 category: { fr: "Visage & corps", en: "Face & body", pt: "Rosto e corpo" },
 image: "/photos/nanda-cosmetiques/ing-aloe-hero.jpg",
 default: true,
 },
 {
 id: "baobab",
 name: { fr: "Huile de Baobab", en: "Baobab Oil", pt: "Óleo de Baobá" },
 category: { fr: "Huile beauté", en: "Beauty oil", pt: "Óleo de beleza" },
 image: "/photos/nanda-cosmetiques/baobab-oil.png",
 default: true,
 },
 {
 id: "moringa",
 name: { fr: "Huile de Moringa", en: "Moringa Oil", pt: "Óleo de Moringa" },
 category: { fr: "Huile beauté", en: "Beauty oil", pt: "Óleo de beleza" },
 image: "/photos/nanda-cosmetiques/moringa-oil.png",
 default: false,
 },
 {
 id: "hibiscus",
 name: { fr: "Huile d'Hibiscus", en: "Hibiscus Oil", pt: "Óleo de Hibisco" },
 category: { fr: "Huile beauté", en: "Beauty oil", pt: "Óleo de beleza" },
 image: "/photos/nanda-cosmetiques/hibiscus-oil.png",
 default: false,
 },
];

const KIT_SIZES = [
 { id: 3, min: 3, max: 3, label: { fr: "3 pièces · Essentiel", en: "3-piece · Essential", pt: "3 peças · Essencial" }, desc: { fr: "Le kit compact, pour un turndown discret.", en: "The compact kit, for a discreet turndown.", pt: "O kit compacto, para um turndown discreto." } },
 { id: 5, min: 5, max: 5, label: { fr: "5 pièces · Signature", en: "5-piece · Signature", pt: "5 peças · Signature" }, desc: { fr: "L'offre complète, pour les suites premium.", en: "The complete offer, for premium suites.", pt: "A oferta completa, para suites premium." } },
 { id: 6, min: 1, max: 6, label: { fr: "Sur mesure", en: "Bespoke", pt: "Sob medida" }, desc: { fr: "Composez librement votre signature.", en: "Compose your signature freely.", pt: "Componha livremente a sua assinatura." } },
];

const LABEL_STYLES = [
 { id: "ivory", label: { fr: "Ivoire", en: "Ivory", pt: "Marfim" }, bg: "#FCFAF6", fg: "#1E2A22", accent: "#B8935E" },
 { id: "kraft", label: { fr: "Kraft naturel", en: "Natural kraft", pt: "Kraft natural" }, bg: "#D4B896", fg: "#3B2A1C", accent: "#7A4F27" },
 { id: "matte", label: { fr: "Noir mat", en: "Matte black", pt: "Preto mate" }, bg: "#17140F", fg: "#F6F1E4", accent: "#B8935E" },
];

const FORMATS = [
 { id: "mini", label: { fr: "Mini · 30 ml", en: "Mini · 30 ml", pt: "Mini · 30 ml" } },
 { id: "medium", label: { fr: "Voyage · 80 ml", en: "Travel · 80 ml", pt: "Viagem · 80 ml" } },
 { id: "wall", label: { fr: "Dispenser · 400 ml", en: "Dispenser · 400 ml", pt: "Dispensador · 400 ml" } },
];

const pick = (obj, locale) => obj[locale] || obj.fr;

const T = (locale) => ({
 breadcrumbCosm: { fr: "Cosmétiques Naturels", en: "Natural Cosmetics", pt: "Cosméticos Naturais" }[locale],
 breadcrumbSelf: { fr: "Kit Amenity", en: "Amenity Kit", pt: "Kit Amenity" }[locale],
 eyebrow: { fr: "Programme I · Hôtels", en: "Programme I · Hotels", pt: "Programa I · Hotéis" }[locale],
 heroTitle: {
 fr: (<>Configurez votre <em className="italic text-[#B8935E]">signature</em> amenity.</>),
 en: (<>Compose your <em className="italic text-[#B8935E]">signature</em> amenity.</>),
 pt: (<>Componha a sua <em className="italic text-[#B8935E]">assinatura</em> amenity.</>),
 }[locale],
 heroLead: {
 fr: "Un vestiaire de six références naturelles, une étiquette à votre nom, un livret trilingue à chaque chevet. Un geste qui raconte l'Afrique de l'Ouest à vos hôtes.",
 en: "A wardrobe of six natural references, a label carrying your name, a trilingual booklet on every bedside. A gesture that tells West Africa to your guests.",
 pt: "Um vestuário de seis referências naturais, uma etiqueta com o seu nome, um livrete trilingue em cada mesa de cabeceira. Um gesto que conta a África Ocidental aos seus hóspedes.",
 }[locale],
 ctaSample: { fr: "Recevoir le kit d'échantillonnage", en: "Receive the sample kit", pt: "Receber o kit de amostragem" }[locale],
 hotelNameLabel: { fr: "Nom de votre maison", en: "Your house name", pt: "Nome da sua casa" }[locale],
 hotelNamePlaceholder: { fr: "Ex. Grand Hôtel de Bissau", en: "e.g. Grand Hôtel de Bissau", pt: "Ex. Grand Hôtel de Bissau" }[locale],
 kitSizeLabel: { fr: "Format du kit", en: "Kit format", pt: "Formato do kit" }[locale],
 productsLabel: { fr: "Références sélectionnées", en: "Selected references", pt: "Referências seleccionadas" }[locale],
 formatLabel: { fr: "Format des flacons", en: "Bottle format", pt: "Formato dos frascos" }[locale],
 labelStyleLabel: { fr: "Étiquette", en: "Label", pt: "Etiqueta" }[locale],
 previewLabel: { fr: "Aperçu en temps réel", en: "Live preview", pt: "Pré-visualização em tempo real" }[locale],
 summaryTitle: { fr: "Votre signature", en: "Your signature", pt: "A sua assinatura" }[locale],
 summaryEmptyHint: { fr: "Sélectionnez vos références pour composer le kit.", en: "Select your references to compose the kit.", pt: "Seleccione as referências para compor o kit." }[locale],
 invalidCountFmt: (n, min, max) => ({
 fr: `Ajustez la sélection : ${min === max ? min : `${min}–${max}`} référence(s) attendue(s), ${n} sélectionnée(s).`,
 en: `Adjust selection: ${min === max ? min : `${min}–${max}`} reference(s) expected, ${n} selected.`,
 pt: `Ajuste a selecção: ${min === max ? min : `${min}–${max}`} referência(s) esperada(s), ${n} seleccionada(s).`,
 }[locale]),
 step: {
 fr: (n, title) => `Étape ${n} · ${title}`,
 en: (n, title) => `Step ${n} · ${title}`,
 pt: (n, title) => `Passo ${n} · ${title}`,
 }[locale],
 ctaFinal: { fr: "Demander un devis pour ce kit", en: "Request a quote for this kit", pt: "Pedir um orçamento para este kit" }[locale],
 ctaSecondary: { fr: "Télécharger le livret B2B (PDF)", en: "Download the B2B booklet (PDF)", pt: "Descarregar o livrete B2B (PDF)" }[locale],
 reset: { fr: "Réinitialiser", en: "Reset", pt: "Reiniciar" }[locale],
});

export default function AmenityKit() {
 const { locale } = useLanguage();
 const t = T(locale);

 const [hotelName, setHotelName] = React.useState("");
 const [kitSizeId, setKitSizeId] = React.useState(5);
 const [labelStyle, setLabelStyle] = React.useState("ivory");
 const [format, setFormat] = React.useState("mini");
 const [selected, setSelected] = React.useState(() => SKUS.filter((s) => s.default).map((s) => s.id));

 const kitSize = KIT_SIZES.find((k) => k.id === kitSizeId);
 const label = LABEL_STYLES.find((l) => l.id === labelStyle);
 const fmt = FORMATS.find((f) => f.id === format);

 // Sync selection to kit size (when kit is not bespoke, enforce exact count)
 React.useEffect(() => {
 if (!kitSize) return;
 if (kitSize.min === kitSize.max) {
 const target = kitSize.min;
 setSelected((prev) => {
 if (prev.length === target) return prev;
 // If too few, add first non-selected default-first order; if too many, trim
 if (prev.length < target) {
 const missing = target - prev.length;
 const toAdd = SKUS.map((s) => s.id).filter((id) => !prev.includes(id)).slice(0, missing);
 return [...prev,...toAdd];
 }
 return prev.slice(0, target);
 });
 }
 }, [kitSizeId]); // eslint-disable-line

 const toggleSku = (id) => {
 setSelected((prev) => {
 const has = prev.includes(id);
 if (has) return prev.filter((x) => x !== id);
 if (kitSize && kitSize.max && prev.length >= kitSize.max) {
 // In fixed-size mode, replace the earliest picked
 return [...prev.slice(1), id];
 }
 return [...prev, id];
 });
 };

 const resetAll = () => {
 setHotelName("");
 setKitSizeId(5);
 setLabelStyle("ivory");
 setFormat("mini");
 setSelected(SKUS.filter((s) => s.default).map((s) => s.id));
 };

 const isValid = selected.length >= kitSize.min && selected.length <= kitSize.max;

 const selectedSkus = SKUS.filter((s) => selected.includes(s.id));

 // Build a mailto for a light "request-a-sample" fallback CTA
 const summaryText = [
 hotelName ? `${t.hotelNameLabel}: ${hotelName}` : "",
 `${t.kitSizeLabel}: ${pick(kitSize.label, locale)}`,
 `${t.labelStyleLabel}: ${pick(label.label, locale)}`,
 `${t.formatLabel}: ${pick(fmt.label, locale)}`,
 `${t.productsLabel}: ${selectedSkus.map((s) => pick(s.name, locale)).join(" · ")}`,
 ].filter(Boolean).join("\n");

 const devisUrl = `/devis?service=amenity-kit&${new URLSearchParams({
 hotel: hotelName || "",
 size: String(kitSize.id),
 label: labelStyle,
 format,
 sku: selected.join(","),
 }).toString}`;

 return (
 <div className="bg-white min-h-screen">
 <script
 type="application/ld+json"
 dangerouslySetInnerHTML={{
 __html: JSON.stringify({
 "@context": "https://schema.org",
 "@type": "Service",
 name: "Hotel Amenity Kit NANDA SÁ",
 provider: { "@type": "Organization", name: "NANDA SÁ", address: { "@type": "PostalAddress", addressCountry: "GW" } },
 areaServed: ["Guinée-Bissau", "West Africa", "Europe"],
 serviceType: "Hotel amenity kit · Private label · Co-branding",
 audience: { "@type": "Audience", audienceType: "Premium hotels, resorts, spas, boutique hospitality groups" },
 }),
 }}
 />

 <Breadcrumbs items={[{ to: "/cosmetiques", label: t.breadcrumbCosm }, { label: t.breadcrumbSelf }]} />

 {/* HERO */}
 <section className="bg-[#FAF7F1] border-t border-nanda-line">
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-16 md:py-24">
 <FadeUp>
 <div className="label-overline text-[#B8935E] mb-4">{t.eyebrow}</div>
 <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[0.98] text-nanda-ink max-w-4xl" data-testid="amenity-hero-title">
 {t.heroTitle}
 </h1>
 <p className="mt-8 max-w-2xl text-lg md:text-xl text-nanda-muted leading-relaxed">
 {t.heroLead}
 </p>
 </FadeUp>
 </div>
 </section>

 {/* CONFIGURATOR */}
 <section className="border-t border-nanda-line bg-white" id="configurator">
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-16 md:py-24">
 <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">

 {/* ─── LEFT · CONFIGURATOR CONTROLS ─── */}
 <div className="lg:col-span-5" data-testid="amenity-controls">

 {/* Step 1 : Hotel name */}
 <div className="mb-10">
 <div className="label-overline text-[#B8935E] mb-3">{t.step(1, t.hotelNameLabel)}</div>
 <input
 type="text"
 value={hotelName}
 onChange={(e) => setHotelName(e.target.value.slice(0, 40))}
 placeholder={t.hotelNamePlaceholder}
 className="w-full px-4 py-3 bg-transparent border-b-2 border-nanda-line font-serif text-2xl focus:outline-none focus:border-[#B8935E] transition-colors text-nanda-ink placeholder:text-nanda-muted/50"
 data-testid="amenity-hotel-name"
 maxLength={40}
 aria-label={t.hotelNameLabel}
 />
 </div>

 {/* Step 2 : Kit size */}
 <div className="mb-10">
 <div className="label-overline text-[#B8935E] mb-4">{t.step(2, t.kitSizeLabel)}</div>
 <div className="grid grid-cols-3 gap-3">
 {KIT_SIZES.map((k) => (
 <button
 key={k.id}
 type="button"
 onClick={() => setKitSizeId(k.id)}
 className={`text-left px-4 py-4 border transition-all ${
 kitSizeId === k.id
 ? "border-[#B8935E] bg-[#B8935E]/5"
 : "border-nanda-line hover:border-[#B8935E]/60"
 }`}
 data-testid={`amenity-kit-size-${k.id}`}
 >
 <div className="font-serif text-lg text-nanda-ink">{pick(k.label, locale)}</div>
 <div className="text-xs text-nanda-muted mt-1 leading-snug">{pick(k.desc, locale)}</div>
 </button>
 ))}
 </div>
 </div>

 {/* Step 3 : Products */}
 <div className="mb-10">
 <div className="flex items-baseline justify-between mb-4">
 <div className="label-overline text-[#B8935E]">{t.step(3, t.productsLabel)}</div>
 <span className={`text-xs tracking-wider uppercase ${isValid ? "text-nanda-muted" : "text-[#A83E2C]"}`} data-testid="amenity-count-status">
 {selected.length}/{kitSize.min === kitSize.max ? kitSize.min : `${kitSize.min}–${kitSize.max}`}
 </span>
 </div>
 <div className="grid grid-cols-2 gap-3">
 {SKUS.map((s) => {
 const isOn = selected.includes(s.id);
 return (
 <button
 key={s.id}
 type="button"
 onClick={() => toggleSku(s.id)}
 className={`group flex items-center gap-3 p-3 border transition-all text-left ${
 isOn ? "border-[#B8935E] bg-[#B8935E]/5" : "border-nanda-line hover:border-[#B8935E]/60"
 }`}
 data-testid={`amenity-sku-${s.id}`}
 aria-pressed={isOn}
 >
 <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-[#F7F3EC]">
 <img src={s.image} alt="" className="max-w-full max-h-full object-contain" loading="lazy" />
 </div>
 <div className="min-w-0 flex-1">
 <div className="font-serif text-sm text-nanda-ink truncate">{pick(s.name, locale)}</div>
 <div className="text-[10px] tracking-wider uppercase text-nanda-muted">{pick(s.category, locale)}</div>
 </div>
 <div
 className={`w-4 h-4 flex-shrink-0 border transition-all ${
 isOn ? "border-[#B8935E] bg-[#B8935E] text-white" : "border-nanda-line"
 } flex items-center justify-center`}
 aria-hidden="true"
 >
 {isOn && <Check className="w-3 h-3" strokeWidth={3} />}
 </div>
 </button>
 );
 })}
 </div>
 {!isValid && (
 <p className="mt-3 text-xs text-[#A83E2C]" data-testid="amenity-count-warning">
 {t.invalidCountFmt(selected.length, kitSize.min, kitSize.max)}
 </p>
 )}
 </div>

 {/* Step 4 : Format */}
 <div className="mb-10">
 <div className="label-overline text-[#B8935E] mb-4">{t.step(4, t.formatLabel)}</div>
 <div className="grid grid-cols-3 gap-3">
 {FORMATS.map((f) => (
 <button
 key={f.id}
 type="button"
 onClick={() => setFormat(f.id)}
 className={`px-3 py-3 border text-center transition-all ${
 format === f.id ? "border-[#B8935E] bg-[#B8935E]/5 text-nanda-ink" : "border-nanda-line text-nanda-muted hover:border-[#B8935E]/60"
 }`}
 data-testid={`amenity-format-${f.id}`}
 >
 <div className="text-xs font-serif">{pick(f.label, locale)}</div>
 </button>
 ))}
 </div>
 </div>

 {/* Step 5 : Label style */}
 <div className="mb-10">
 <div className="label-overline text-[#B8935E] mb-4">{t.step(5, t.labelStyleLabel)}</div>
 <div className="grid grid-cols-3 gap-3">
 {LABEL_STYLES.map((l) => (
 <button
 key={l.id}
 type="button"
 onClick={() => setLabelStyle(l.id)}
 className={`px-3 py-4 border text-center transition-all ${
 labelStyle === l.id ? "border-[#B8935E]" : "border-nanda-line hover:border-[#B8935E]/60"
 }`}
 data-testid={`amenity-label-${l.id}`}
 >
 <div
 className="w-full h-10 mb-2 flex items-center justify-center text-[8px] tracking-[0.3em] uppercase"
 style={{ background: l.bg, color: l.fg }}
 >
 <span style={{ color: l.accent }}>· NANDA SÁ ·</span>
 </div>
 <div className="text-xs font-serif text-nanda-ink">{pick(l.label, locale)}</div>
 </button>
 ))}
 </div>
 </div>

 <button
 type="button"
 onClick={resetAll}
 className="text-xs tracking-[0.25em] uppercase text-nanda-muted hover:text-[#B8935E] transition-colors"
 data-testid="amenity-reset"
 >
 ↺ {t.reset}
 </button>
 </div>

 {/* ─── RIGHT · LIVE PREVIEW ─── */}
 <div className="lg:col-span-7">
 <div className="sticky top-24">
 <div className="label-overline text-[#B8935E] mb-4">{t.previewLabel}</div>

 <div
 className="relative overflow-hidden transition-colors duration-500"
 style={{
 background: label.bg === "#FCFAF6" ? "#EFE7D8" : label.bg === "#D4B896" ? "#C4A57F" : "#0F0C09",
 minHeight: 520,
 }}
 data-testid="amenity-preview-stage"
 >
 {/* subtle diagonal grain */}
 <div
 className="absolute inset-0 opacity-[0.08] pointer-events-none"
 style={{
 backgroundImage:
 "repeating-linear-gradient(45deg, transparent 0, transparent 2px, currentColor 2px, currentColor 3px)",
 color: label.fg,
 }}
 />

 {/* Bottles grid */}
 <div className="relative flex items-end justify-center gap-4 md:gap-6 px-6 py-16 flex-wrap min-h-[400px]">
 {selectedSkus.length === 0 && (
 <div className="w-full text-center py-16" style={{ color: label.fg, opacity: 0.6 }}>
 <Sparkles className="w-8 h-8 mx-auto mb-4 opacity-40" />
 <p className="font-serif italic text-lg">{t.summaryEmptyHint}</p>
 </div>
 )}
 {selectedSkus.map((sku, i) => (
 <div
 key={sku.id}
 className="relative flex flex-col items-center transition-all duration-500"
 style={{
 animation: `bottleIn 0.5s ${i * 60}ms both`,
 }}
 data-testid={`amenity-preview-bottle-${sku.id}`}
 >
 <div
 className="relative w-24 md:w-28 h-40 md:h-48 bg-white flex items-center justify-center overflow-hidden"
 style={{
 filter: label.id === "matte" ? "brightness(0.92) contrast(1.08)" : "none",
 }}
 >
 <img
 src={sku.image}
 alt={pick(sku.name, locale)}
 className="w-full h-full object-contain p-2"
 loading="lazy"
 />
 {/* Overlay label band */}
 <div
 className="absolute inset-x-2 bottom-4 py-1.5 px-1 text-center flex flex-col items-center justify-center gap-0.5"
 style={{
 background: label.bg,
 color: label.fg,
 border: `0.5px solid ${label.accent}`,
 }}
 >
 <div
 className="text-[6px] md:text-[7px] tracking-[0.2em] uppercase leading-tight truncate w-full"
 style={{ color: label.accent }}
 >
 {hotelName || "NANDA SÁ"}
 </div>
 <div className="text-[7px] md:text-[8px] font-serif italic leading-tight truncate w-full">
 {pick(sku.name, locale).split("&")[0].trim().slice(0, 16)}
 </div>
 </div>
 </div>
 <div
 className="mt-3 text-[9px] md:text-[10px] tracking-[0.2em] uppercase text-center max-w-[110px] leading-tight"
 style={{ color: label.fg, opacity: 0.75 }}
 >
 {pick(sku.category, locale)}
 </div>
 </div>
 ))}
 </div>

 {/* Kit signature strip */}
 <div
 className="absolute left-0 right-0 bottom-0 px-6 py-4 border-t flex items-center justify-between"
 style={{ borderColor: `${label.fg}20`, background: `${label.bg}10` }}
 >
 <div style={{ color: label.fg }}>
 <div className="text-[9px] tracking-[0.3em] uppercase opacity-60">Amenity Kit · Volume I</div>
 <div className="font-serif text-lg mt-0.5 truncate max-w-xs">
 {hotelName || <em className="italic opacity-60">{t.hotelNamePlaceholder}</em>}
 </div>
 </div>
 <div className="text-right" style={{ color: label.accent }}>
 <div className="text-[9px] tracking-[0.3em] uppercase opacity-70">NANDA SÁ</div>
 <div className="text-[8px] tracking-widest opacity-60">Guiné-Bissau</div>
 </div>
 </div>
 </div>

 {/* Under-preview summary */}
 <div className="mt-6 flex items-center justify-between gap-4 flex-wrap">
 <div className="text-xs text-nanda-muted uppercase tracking-wider">
 {selectedSkus.length} × {pick(fmt.label, locale)} · {pick(label.label, locale)}
 </div>
 <div className="text-xs text-[#B8935E] uppercase tracking-wider">{pick(kitSize.label, locale)}</div>
 </div>
 </div>
 </div>
 </div>
 </div>
 </section>

 {/* SUMMARY + CTAs */}
 <section className="border-t border-nanda-line bg-[#1E2A22] text-white">
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-20 md:py-24">
 <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
 <div className="lg:col-span-5">
 <div className="label-overline text-[#B8935E] mb-4">{t.summaryTitle}</div>
 <h2 className="font-serif text-4xl md:text-5xl leading-tight">
 {{
 fr: (<>Un devis en <em className="italic text-[#B8935E]">cinq jours ouvrés</em>.</>),
 en: (<>A quote in <em className="italic text-[#B8935E]">five working days</em>.</>),
 pt: (<>Um orçamento em <em className="italic text-[#B8935E]">cinco dias úteis</em>.</>),
 }[locale]}
 </h2>
 <p className="mt-6 text-white/70 leading-relaxed max-w-md">
 {{
 fr: "Envoyez-nous votre configuration. Un contact dédié vous répond avec une proposition d'échantillonnage, un calendrier et les MOQ associés.",
 en: "Send us your configuration. A dedicated contact will reply with a sampling proposal, a timeline and the associated MOQs.",
 pt: "Envie-nos a sua configuração. Um contacto dedicado responde com proposta de amostragem, calendário e MOQs associados.",
 }[locale]}
 </p>
 </div>

 <div className="lg:col-span-7">
 <pre
 className="whitespace-pre-wrap font-sans text-sm text-white/85 leading-relaxed border-t border-white/20 pt-6"
 data-testid="amenity-summary-text"
 >{summaryText}</pre>

 <div className="flex flex-wrap items-center gap-4 mt-10">
 <Link
 to={devisUrl}
 className={`inline-flex items-center gap-2 px-8 py-4 text-sm tracking-wider uppercase transition-colors ${
 isValid
 ? "bg-[#B8935E] text-white hover:bg-white hover:text-[#1E2A22]"
 : "bg-white/10 text-white/40 pointer-events-none"
 }`}
 data-testid="amenity-cta-devis"
 onClick={(e) => { if (!isValid) e.preventDefault; }}
 aria-disabled={!isValid}
 >
 {t.ctaFinal}
 <ArrowUpRight className="w-4 h-4" />
 </Link>

 <a
 href="/pdf/nanda-cosmetics-b2b-distribution-2026.pdf"
 target="_blank"
 rel="noreferrer"
 className="inline-flex items-center gap-2 px-6 py-4 text-xs tracking-wider uppercase border border-white/30 hover:border-white transition-colors text-white/80 hover:text-white"
 data-testid="amenity-cta-pdf"
 >
 {t.ctaSecondary}
 </a>
 </div>
 </div>
 </div>
 </div>
 </section>

 <style>{`
 @keyframes bottleIn {
 from { opacity: 0; transform: translateY(12px); }
 to { opacity: 1; transform: translateY(0); }
 }
 `}</style>
 </div>
 );
}
